import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import TextInput from '../../elements/forms/TextInput';
import ButtonComponent from '../../elements/forms/ButtonComponent';
import { FaLock, FaEnvelope, FaEye, FaEyeSlash, FaHashtag } from 'react-icons/fa';
import { create_candidate_password } from '../../../service/api/candidate';
import { ICreateCandidatePasswordError } from '../../../store/types/candidate';

export type ICreateCandidatePasswordModalProps = {
  open: boolean;
  candidateEmail?: string;
  candidateNumber?: string;
  candidateName?: string;
  onCancel: () => void;
  onSuccess?: () => void;
};

const CreateCandidatePasswordModal: React.FC<ICreateCandidatePasswordModalProps> = ({
  open,
  candidateEmail = '',
  candidateNumber = '',
  candidateName,
  onCancel,
  onSuccess
}) => {
  const [email, setEmail] = useState<string>(candidateEmail);
  const [candNumber, setCandNumber] = useState<string>(candidateNumber);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  const [errors, setErrors] = useState<ICreateCandidatePasswordError>({
    email: '',
    candidate_number: '',
    password: '',
    password_confirmation: ''
  });

  useEffect(() => {
    if (open) {
      setEmail(candidateEmail);
      setCandNumber(candidateNumber);
      setPassword('');
      setPasswordConfirmation('');
      setValidationError('');
      setErrors({
        email: '',
        candidate_number: '',
        password: '',
        password_confirmation: ''
      });
    }
  }, [open, candidateEmail, candidateNumber]);

  const handleSubmit = async () => {
    setValidationError('');
    setErrors({
      email: '',
      candidate_number: '',
      password: '',
      password_confirmation: ''
    });

    let hasError = false;
    const newErrors: ICreateCandidatePasswordError = {
      email: '',
      candidate_number: '',
      password: '',
      password_confirmation: ''
    };

    if (!email.trim() && !candNumber.trim()) {
      hasError = true;
      newErrors.email = 'warning';
      newErrors.candidate_number = 'warning';
      setValidationError('Please provide candidate email or candidate number.');
    }

    if (!password) {
      hasError = true;
      newErrors.password = 'warning';
      if (!validationError) setValidationError('Password is required.');
    } else if (password.length < 6) {
      hasError = true;
      newErrors.password = 'warning';
      setValidationError('Password must be at least 6 characters long.');
    }

    if (password !== passwordConfirmation) {
      hasError = true;
      newErrors.password_confirmation = 'warning';
      setValidationError('Passwords do not match.');
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const success = await create_candidate_password({
      email: email.trim() || undefined,
      candidate_number: candNumber.trim() || undefined,
      password,
      password_confirmation: passwordConfirmation
    });

    setLoading(false);

    if (success) {
      setPassword('');
      setPasswordConfirmation('');
      if (onSuccess) onSuccess();
      onCancel();
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      closeIcon={false}
      centered={true}
      maskClosable={!loading}
      onCancel={loading ? undefined : onCancel}
    >
      <div className="p-2 font-abel text-[#4E4E4E]">
        <div className="mb-4 pb-2 border-b border-[#E9EAEF]">
          <h2 className="font-bold text-xl text-[#243677]">
            Set Candidate Password
          </h2>
          {candidateName && (
            <p className="text-sm text-gray-500 mt-1">
              Candidate: <span className="font-semibold text-[#243677]">{candidateName}</span>
            </p>
          )}
        </div>

        {validationError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
            {validationError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <TextInput
              label="Candidate Email"
              name="email"
              placeholder="Candidate Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              status={errors.email}
              icon={<FaEnvelope size={18} color="#567BFF" />}
            />
          </div>

          <div>
            <TextInput
              label="Candidate Number"
              name="candidate_number"
              placeholder="Candidate Number (Optional if Email is set)"
              value={candNumber}
              onChange={(e) => setCandNumber(e.target.value)}
              status={errors.candidate_number}
              icon={<FaHashtag size={18} color="#567BFF" />}
            />
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <TextInput
                label="New Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                status={errors.password}
                required={true}
                icon={<FaLock size={18} color="#243677" />}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="mt-6 p-3 text-gray-500 hover:text-[#243677] transition-colors"
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <TextInput
                label="Confirm Password"
                name="password_confirmation"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                status={errors.password_confirmation}
                required={true}
                icon={<FaLock size={18} color="#567BFF" />}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="mt-6 p-3 text-gray-500 hover:text-[#243677] transition-colors"
              title={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </button>
          </div>
        </div>

        <div className="mt-8 flex justify-between space-x-4">
          <div className="w-[48%]">
            <ButtonComponent
              title="Set Password"
              onHandler={handleSubmit}
              active={!loading}
              loading={loading}
              backgroundColor="#243677"
            />
          </div>
          <div className="w-[48%]">
            <ButtonComponent
              title="Cancel"
              onHandler={onCancel}
              backgroundColor="red"
              active={!loading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateCandidatePasswordModal;
